import { memo, useEffect, useRef } from 'react'
import { Grid, useTheme } from '@mui/material'
import { DataGridPro, GRID_TREE_DATA_GROUPING_FIELD, GridApiPro, GridColDef, GridFilterModel, GridRenderCellParams, GridRowsProp } from '@mui/x-data-grid-pro'

import useStickyHeaders from '../../../../utils/hooks/useStickyHeaders'

import useStyles from './index.styles'
import LinkRow from './link-row'

interface IProps {
  gridRef: React.RefObject<GridApiPro>
  rows: GridRowsProp
  columns: GridColDef[]
  isLoading: boolean
  filterModel: GridFilterModel
  shouldShowParticipantView: boolean
  searchText: string
}

const FolderGrid = memo(({ gridRef, rows, columns, isLoading, filterModel, shouldShowParticipantView, searchText }: IProps) => {
  const classes = useStyles
  const theme = useTheme()
  const containerRef = useRef(null)
  useStickyHeaders({ containerRef })

  useEffect(() => {
    if (searchText) {
      gridRef.current?.expandAllRows()
    } else {
      gridRef.current?.collapseAllRows()
    }
  }, [gridRef, searchText])

  return (
    <Grid
      sx={{
        flexDirection: 'column',
      }}
    >
      <DataGridPro
        ref={containerRef}
        apiRef={gridRef}
        treeData
        getTreeDataPath={(row) => row.hierarchy}
        rows={rows as IFolderRow[]}
        columns={columns}
        loading={isLoading}
        groupingColDef={{
          headerName: 'Name',
          flex: 6,
          sortable: true,
          renderCell: (params: GridRenderCellParams<IFolderRow>) => {
            const { row, rowNode } = params
            const depth = row.hierarchy.length
            const isOpen = rowNode.type === 'group' && Boolean(rowNode.childrenExpanded)
            const setIsOpen = rowNode.type === 'group' ? () => gridRef.current.setRowChildrenExpansion(row.id, !isOpen) : () => {}
            return <LinkRow item={row.id} isOpen={isOpen} setIsOpen={setIsOpen} depth={depth} />
          },
        }}
        initialState={{
          sorting: {
            sortModel: [
              { field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'asc' },
              { field: 'status', sort: 'asc' },
              { field: 'alerts', sort: 'asc' },
              { field: 'devices', sort: 'asc' },
              { field: 'contacts', sort: 'asc' },
            ],
          },
        }}
        filterModel={filterModel}
        sx={{
          ...classes.dataGrid(theme),
          '& .rightAlignedColumnHeader': classes.rightAlignedColumnHeader,
          '& .centeredColumnHeader': classes.centeredColumnHeader,
        }}
        rowHeight={shouldShowParticipantView ? 64 : 52}
        autosizeOptions={{ expand: true }}
        disableRowSelectionOnClick
        hideFooter
      />
    </Grid>
  )
})

export default FolderGrid
